import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface AdminInvitationQueryFilters {
  status?: UserService.InvitationsGetQueryFiltersParameterStatusEnum | string
  organizationId?: string
}

export interface UseAdminInvitationsParams {
  queryFilters: AdminInvitationQueryFilters
  sortField?: keyof UserService.Invitation
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminInvitationsQueryKeys = {
  all: () => ['admin-invitations'] as const,
  list: (params: { queryFilters: AdminInvitationQueryFilters; sortField?: keyof UserService.Invitation; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-invitations', params] as const,
}

const fetchAdminInvitationsPage = async ({
  pageParam,
  queryFilters,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  queryFilters: AdminInvitationQueryFilters
  sortField?: keyof UserService.Invitation
  sortDirection?: 'asc' | 'desc'
}): Promise<UserService.Invitation[]> => {
  const { limit, offset } = pageParam
  const response = await apis.userService.invitationsGet(
    queryFilters as UserService.InvitationsGetQueryFiltersParameter,
    sortField as UserService.InvitationSortFieldEnum | undefined,
    sortDirection as UserService.SortDirectionEnum | undefined,
    limit,
    offset
  )
  return response.data
}

export const useAdminInvitations = ({
  queryFilters,
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminInvitationsParams) => {
  const query = useInfiniteQuery({
    queryKey: adminInvitationsQueryKeys.list({ queryFilters, sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminInvitationsPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        queryFilters,
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined
      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit = allPages.length === 1
        ? initialPageSize * 2
        : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)
      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const invitations = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    invitations,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
